import * as THREE from 'three';
import { Utils } from '../utils.js';
import { BoxVolume, PlaneVolume } from './Volume.js';

export class TransformationTool {
    constructor(viewer) {
        this.viewer = viewer;

        this.scene = new THREE.Scene();

        this.selection = [];
        this.pivot = new THREE.Vector3();
        this.dragging = false;
        this.showPickVolumes = false;
        this.stickyTags = false;

        this.viewer.inputHandler.registerInteractiveScene(this.scene);
        this.viewer.inputHandler.addEventListener('selection_changed', (e) => {
            for (let selected of this.selection) {
                this.viewer.inputHandler.blacklist.delete(selected);
            }

            this.selection = e.selection;

            for (let selected of this.selection) {
                this.viewer.inputHandler.blacklist.add(selected);
            }
        });

        let red = 0xe73100;
        let green = 0x66ff66;
        let blue = 0x2669e7;
        let white = 0xffffff;

        this.activeHandle = null;
        this.scaleHandles = {
            'scale.x+': { name: 'scale.x+', node: new THREE.Object3D(), color: red, alignment: [+1, +0, +0] },
            'scale.x-': { name: 'scale.x-', node: new THREE.Object3D(), color: red, alignment: [-1, +0, +0] },
            'scale.y+': { name: 'scale.y+', node: new THREE.Object3D(), color: green, alignment: [+0, +1, +0] },
            'scale.y-': { name: 'scale.y-', node: new THREE.Object3D(), color: green, alignment: [+0, -1, +0] },
            'scale.z+': { name: 'scale.z+', node: new THREE.Object3D(), color: blue, alignment: [+0, +0, +1] },
            'scale.z-': { name: 'scale.z-', node: new THREE.Object3D(), color: blue, alignment: [+0, +0, -1] },
        };
        this.focusHandles = {
            'focus.x+': { name: 'focus.x+', node: new THREE.Object3D(), color: red, alignment: [+1, +0, +0] },
            'focus.x-': { name: 'focus.x-', node: new THREE.Object3D(), color: red, alignment: [-1, +0, +0] },
            'focus.y+': { name: 'focus.y+', node: new THREE.Object3D(), color: green, alignment: [+0, +1, +0] },
            'focus.y-': { name: 'focus.y-', node: new THREE.Object3D(), color: green, alignment: [+0, -1, +0] },
            'focus.z+': { name: 'focus.z+', node: new THREE.Object3D(), color: blue, alignment: [+0, +0, +1] },
            'focus.z-': { name: 'focus.z-', node: new THREE.Object3D(), color: blue, alignment: [+0, +0, -1] },
        };

        this.lockHandles = {
            'lock.x+': { name: 'lock.x+', node: new THREE.Object3D(), color: white, alignment: [+1, +0, +0] },
            'lock.x-': { name: 'lock.x-', node: new THREE.Object3D(), color: white, alignment: [-1, +0, +0] },
            'lock.y+': { name: 'lock.y+', node: new THREE.Object3D(), color: white, alignment: [+0, +1, +0] },
            'lock.y-': { name: 'lock.y-', node: new THREE.Object3D(), color: white, alignment: [+0, -1, +0] },
            'lock.z+': { name: 'lock.z+', node: new THREE.Object3D(), color: white, alignment: [+0, +0, +1] },
            'lock.z-': { name: 'lock.z-', node: new THREE.Object3D(), color: white, alignment: [+0, +0, -1] },
        };

        this.textureHandles = {
            'texture.x+': {
                name: 'texture.x+',
                node: new THREE.Object3D(),
                color: red,
                alignment: [+1, +0, +0],
                transparentBackground: false,
            },
            'texture.x-': {
                name: 'texture.x-',
                node: new THREE.Object3D(),
                color: red,
                alignment: [-1, +0, +0],
                transparentBackground: false,
            },
            'texture.y+': {
                name: 'texture.y+',
                node: new THREE.Object3D(),
                color: green,
                alignment: [+0, +1, +0],
                transparentBackground: false,
            },
            'texture.y-': {
                name: 'texture.y-',
                node: new THREE.Object3D(),
                color: green,
                alignment: [+0, -1, +0],
                transparentBackground: false,
            },
            'texture.z+': {
                name: 'texture.z+',
                node: new THREE.Object3D(),
                color: blue,
                alignment: [+0, +0, +1],
                transparentBackground: false,
            },
            'texture.z-': {
                name: 'texture.z-',
                node: new THREE.Object3D(),
                color: blue,
                alignment: [+0, +0, -1],
                transparentBackground: false,
            },
        };
        this.translationHandles = {
            'translation.x': { name: 'translation.x', node: new THREE.Object3D(), color: red, alignment: [1, 0, 0] },
            'translation.y': { name: 'translation.y', node: new THREE.Object3D(), color: green, alignment: [0, 1, 0] },
            'translation.z': { name: 'translation.z', node: new THREE.Object3D(), color: blue, alignment: [0, 0, 1] },
        };
        this.rotationHandles = {
            'rotation.x': { name: 'rotation.x', node: new THREE.Object3D(), color: red, alignment: [1, 0, 0] },
            'rotation.y': { name: 'rotation.y', node: new THREE.Object3D(), color: green, alignment: [0, 1, 0] },
            'rotation.z': { name: 'rotation.z', node: new THREE.Object3D(), color: blue, alignment: [0, 0, 1] },
        };
        this.handles = Object.assign(
            {},
            this.scaleHandles,
            this.focusHandles,
            this.lockHandles,
            this.textureHandles,
            this.translationHandles,
            this.rotationHandles
        );
        this.pickVolumes = [];

        this.initializeScaleHandles();
        this.initializeFocusHandles();
        this.initializeLockHandles();
        this.initializeTextureHandles();
        this.initializeTranslationHandles();
        this.initializeRotationHandles();

        let boxFrameVertices = [];
        {
            // bottom
            boxFrameVertices.push(new THREE.Vector3(-0.5, -0.5, 0.5));
            boxFrameVertices.push(new THREE.Vector3(0.5, -0.5, 0.5));
            boxFrameVertices.push(new THREE.Vector3(0.5, -0.5, 0.5));
            boxFrameVertices.push(new THREE.Vector3(0.5, -0.5, -0.5));
            boxFrameVertices.push(new THREE.Vector3(0.5, -0.5, -0.5));
            boxFrameVertices.push(new THREE.Vector3(-0.5, -0.5, -0.5));
            boxFrameVertices.push(new THREE.Vector3(-0.5, -0.5, -0.5));
            boxFrameVertices.push(new THREE.Vector3(-0.5, -0.5, 0.5));
            // top
            boxFrameVertices.push(new THREE.Vector3(-0.5, 0.5, 0.5));
            boxFrameVertices.push(new THREE.Vector3(0.5, 0.5, 0.5));
            boxFrameVertices.push(new THREE.Vector3(0.5, 0.5, 0.5));
            boxFrameVertices.push(new THREE.Vector3(0.5, 0.5, -0.5));
            boxFrameVertices.push(new THREE.Vector3(0.5, 0.5, -0.5));
            boxFrameVertices.push(new THREE.Vector3(-0.5, 0.5, -0.5));
            boxFrameVertices.push(new THREE.Vector3(-0.5, 0.5, -0.5));
            boxFrameVertices.push(new THREE.Vector3(-0.5, 0.5, 0.5));
            // sides
            boxFrameVertices.push(new THREE.Vector3(-0.5, -0.5, 0.5));
            boxFrameVertices.push(new THREE.Vector3(-0.5, 0.5, 0.5));
            boxFrameVertices.push(new THREE.Vector3(0.5, -0.5, 0.5));
            boxFrameVertices.push(new THREE.Vector3(0.5, 0.5, 0.5));
            boxFrameVertices.push(new THREE.Vector3(0.5, -0.5, -0.5));
            boxFrameVertices.push(new THREE.Vector3(0.5, 0.5, -0.5));
            boxFrameVertices.push(new THREE.Vector3(-0.5, -0.5, -0.5));
            boxFrameVertices.push(new THREE.Vector3(-0.5, 0.5, -0.5));
        }
        let boxFrameGeometry = new THREE.BufferGeometry().setFromPoints(boxFrameVertices);
        this.frame = new THREE.LineSegments(boxFrameGeometry, new THREE.LineBasicMaterial({ color: 0xffff00 }));
        this.scene.add(this.frame);
    }

    initializeScaleHandles() {
        let sgSphere = new THREE.SphereGeometry(1.4, 32, 32);
        let sgLowPolySphere = new THREE.SphereGeometry(1.4, 16, 16);

        for (let handleName of Object.keys(this.scaleHandles)) {
            let handle = this.scaleHandles[handleName];
            let node = handle.node;
            this.scene.add(node);
            node.position.set(...handle.alignment).multiplyScalar(0.5);

            let material = new THREE.MeshBasicMaterial({
                color: handle.color,
                opacity: 0.4,
                transparent: true,
            });

            let outlineMaterial = new THREE.MeshBasicMaterial({
                color: 0xcccccc,
                side: THREE.BackSide,
                opacity: 0.4,
                transparent: true,
            });

            let pickMaterial = new THREE.MeshNormalMaterial({
                opacity: 0.2,
                transparent: true,
                visible: this.showPickVolumes,
            });

            let sphere = new THREE.Mesh(sgSphere, material);
            sphere.scale.set(1.3, 1.3, 1.3);
            sphere.name = `${handleName}.handle`;
            sphere.renderOrder = 1;
            node.add(sphere);

            let outline = new THREE.Mesh(sgSphere, outlineMaterial);
            outline.scale.set(1.1, 1.1, 1.1);
            outline.name = `${handleName}.outline`;
            sphere.add(outline);

            let pickSphere = new THREE.Mesh(sgLowPolySphere, pickMaterial);
            pickSphere.name = `${handleName}.pick_volume`;
            pickSphere.scale.set(1, 1, 1);
            pickSphere.renderOrder = 1;
            sphere.add(pickSphere);
            pickSphere.handle = handleName;
            this.pickVolumes.push(pickSphere);

            node.setOpacity = (target) => {
                let opacity = { x: material.opacity };
                let t = new TWEEN.Tween(opacity).to({ x: target }, 100);
                t.onUpdate(() => {
                    sphere.visible = opacity.x > 0;
                    pickSphere.visible = opacity.x > 0;
                    material.opacity = opacity.x;
                    outlineMaterial.opacity = opacity.x;
                    pickSphere.material.opacity = opacity.x * 0.5;
                });
                t.start();
            };

            pickSphere.addEventListener('drag', (e) => this.dragScaleHandle(e));
            pickSphere.addEventListener('drop', (e) => this.dropScaleHandle(e));

            pickSphere.addEventListener('mouseover', (e) => {});

            pickSphere.addEventListener('click', (e) => {
                e.consume();
            });

            pickSphere.addEventListener('mouseleave', (e) => {});
        }
    }

    initializeFocusHandles() {
        let sgPlane = new THREE.PlaneGeometry(4, 4, 1, 1);
        let sgLowPolySphere = new THREE.SphereGeometry(1, 16, 16);

        let texture = new THREE.TextureLoader().load(`${exports.resourcePath}/icons/close.svg`);

        for (let handleName of Object.keys(this.focusHandles)) {
            let handle = this.focusHandles[handleName];
            let node = handle.node;
            this.scene.add(node);
            let align = handle.alignment;

            node.lookAt(new THREE.Vector3(...align));

            let off = -0.8;
            if (align[0] === 1) {
                node.position.set(1, -off, -off).multiplyScalar(0.5);
                node.rotation.z = Math.PI / 2;
            } else if (align[0] === -1) {
                node.position.set(-1, off, -off).multiplyScalar(0.5);
                node.rotation.z = Math.PI / 2;
            } else if (align[1] === 1) {
                node.position.set(off, 1, -off).multiplyScalar(0.5);
                node.rotation.set(Math.PI / 2, Math.PI, 0.0);
            } else if (align[1] === -1) {
                node.position.set(-off, -1, -off).multiplyScalar(0.5);
                node.rotation.set(Math.PI / 2, 0.0, 0.0);
            } else if (align[2] === 1) {
                node.position.set(off, -off, 1).multiplyScalar(0.5);
            } else if (align[2] === -1) {
                node.position.set(-off, -off, -1).multiplyScalar(0.5);
            }

            let material = new THREE.MeshBasicMaterial({
                color: handle.color,
                opacity: 0,
                transparent: true,
                map: texture,
            });

            let pickMaterial = new THREE.MeshNormalMaterial({
                transparent: true,
                visible: this.showPickVolumes,
            });

            let box = new THREE.Mesh(sgPlane, material);
            box.name = `${handleName}.handle`;
            box.scale.set(1, 1, 1);
            box.position.set(0, 0, 0);
            box.visible = false;
            node.add(box);

            let pickSphere = new THREE.Mesh(sgLowPolySphere, pickMaterial);
            pickSphere.name = `${handleName}.pick_volume`;
            pickSphere.scale.set(1, 1, 1);
            box.add(pickSphere);
            pickSphere.handle = handleName;
            this.pickVolumes.push(pickSphere);

            node.setOpacity = (target) => {
                let opacity = { x: material.opacity };
                let t = new TWEEN.Tween(opacity).to({ x: target }, 100);
                t.onUpdate(() => {
                    pickSphere.visible = opacity.x > 0;
                    box.visible = opacity.x > 0;
                    material.opacity = opacity.x;
                    pickSphere.material.opacity = opacity.x * 0.5;
                });
                t.start();
            };

            pickSphere.addEventListener('drag', (e) => {});

            pickSphere.addEventListener('mouseup', (e) => {
                e.consume();
            });

            pickSphere.addEventListener('mousedown', (e) => {
                e.consume();
            });

            pickSphere.addEventListener('click', (e) => {
                let volume = this.selection[0];
                if (volume.clip) {
                    this.viewer.scene.removeVolume(volume);
                } else if (volume instanceof BoxVolume) {
                    if (confirm('Delete custom asset?')) {
                        this.viewer.scene.removeVolume(volume);
                    }
                } else if (volume instanceof PlaneVolume) {
                    if (confirm('Delete plane asset?')) {
                        this.viewer.scene.removePlane(volume);
                    }
                }
            });

            pickSphere.addEventListener('mouseover', (e) => {});

            pickSphere.addEventListener('mouseleave', (e) => {});
        }
    }

    initializeLockHandles() {
        let sgPlane = new THREE.PlaneGeometry(4, 4, 1, 1);
        let sgLowPolySphere = new THREE.SphereGeometry(1, 16, 16);

        let texture = new THREE.TextureLoader().load(`${exports.resourcePath}/icons/lock.svg`);

        for (let handleName of Object.keys(this.lockHandles)) {
            let handle = this.lockHandles[handleName];
            let node = handle.node;
            this.scene.add(node);
            let align = handle.alignment;

            node.lookAt(new THREE.Vector3(...align));

            let off = -0.8;
            if (align[0] === 1) {
                node.position.set(1, -off, off).multiplyScalar(0.5);
                node.rotation.z = Math.PI / 2;
            } else if (align[0] === -1) {
                node.position.set(-1, off, off).multiplyScalar(0.5);
                node.rotation.z = Math.PI / 2;
            } else if (align[1] === 1) {
                node.position.set(off, 1, off).multiplyScalar(0.5);
                node.rotation.set(Math.PI / 2, Math.PI, 0.0);
            } else if (align[1] === -1) {
                node.position.set(-off, -1, off).multiplyScalar(0.5);
                node.rotation.set(Math.PI / 2, 0.0, 0.0);
            } else if (align[2] === 1) {
                node.position.set(-off, -off, 1).multiplyScalar(0.5);
            } else if (align[2] === -1) {
                node.position.set(off, -off, -1).multiplyScalar(0.5);
            }

            let material = new THREE.MeshBasicMaterial({
                color: handle.color,
                opacity: 0,
                transparent: true,
                map: texture,
            });

            let pickMaterial = new THREE.MeshNormalMaterial({
                transparent: true,
                visible: this.showPickVolumes,
            });

            let box = new THREE.Mesh(sgPlane, material);
            box.name = `${handleName}.handle`;
            box.scale.set(1.5, 1.5, 1.5);
            box.position.set(0, 0, 0);
            box.visible = false;
            node.add(box);

            let pickSphere = new THREE.Mesh(sgLowPolySphere, pickMaterial);
            pickSphere.name = `${handleName}.pick_volume`;
            pickSphere.scale.set(2.5, 2.5, 2.5);
            box.add(pickSphere);
            pickSphere.handle = handleName;
            this.pickVolumes.push(pickSphere);

            node.setOpacity = (target) => {
                let opacity = { x: material.opacity };
                let t = new TWEEN.Tween(opacity).to({ x: target }, 100);
                t.onUpdate(() => {
                    pickSphere.visible = opacity.x > 0;
                    box.visible = opacity.x > 0;
                    material.opacity = opacity.x;
                    pickSphere.material.opacity = opacity.x * 0.5;
                });
                t.start();
            };

            node.setOpacity(0.4);

            pickSphere.addEventListener('drag', (e) => {});

            pickSphere.addEventListener('mouseup', (e) => {
                e.consume();
            });

            pickSphere.addEventListener('mousedown', (e) => {
                e.consume();
            });

            pickSphere.addEventListener('click', (e) => {
                let volume = this.selection[0];
                if (!volume.clip) {
                    volume.locked = !volume.locked;
                }
            });

            pickSphere.addEventListener('mouseover', (e) => {});

            pickSphere.addEventListener('mouseleave', (e) => {});
        }
    }

    initializeTextureHandles() {
        let sgPlane = new THREE.PlaneGeometry(4, 4, 1, 1);
        let sgLowPolySphere = new THREE.SphereGeometry(1, 16, 16);

        let texture = new THREE.TextureLoader().load(`${exports.resourcePath}/icons/send-backwards.png`);
        this.activeNode = null;

        for (let handleName of Object.keys(this.textureHandles)) {
            let handle = this.textureHandles[handleName];
            let node = handle.node;
            this.scene.add(node);
            let align = handle.alignment;
            node.lookAt(new THREE.Vector3(...align));

            let off = -0.8;
            if (align[0] === 1) {
                node.position.set(1, off, -off).multiplyScalar(0.5);
                node.rotation.z = Math.PI / 2;
            } else if (align[0] === -1) {
                node.position.set(-1, -off, -off).multiplyScalar(0.5);
                node.rotation.z = Math.PI / 2;
            } else if (align[1] === 1) {
                node.position.set(-off, 1, -off).multiplyScalar(0.5);
                node.rotation.set(Math.PI / 2, Math.PI, 0.0);
            } else if (align[1] === -1) {
                node.position.set(off, -1, -off).multiplyScalar(0.5);
                node.rotation.set(Math.PI / 2, 0.0, 0.0);
            } else if (align[2] === 1) {
                node.position.set(off, off, 1).multiplyScalar(0.5);
            } else if (align[2] === -1) {
                node.position.set(-off, off, -1).multiplyScalar(0.5);
            }

            let material = new THREE.MeshBasicMaterial({
                color: handle.color,
                opacity: 0,
                transparent: true,
                map: texture,
            });

            let pickMaterial = new THREE.MeshNormalMaterial({
                transparent: true,
                visible: this.showPickVolumes,
            });

            let box = new THREE.Mesh(sgPlane, material);
            box.name = `${handleName}.handle`;
            box.scale.set(2, 2, 2);
            box.position.set(0, 0, 0);
            box.visible = false;
            node.add(box);

            let pickSphere = new THREE.Mesh(sgLowPolySphere, pickMaterial);
            pickSphere.name = `${handleName}.pick_volume`;
            pickSphere.scale.set(2, 2, 2);
            box.add(pickSphere);
            pickSphere.handle = handleName;
            this.pickVolumes.push(pickSphere);

            node.setOpacity = (target) => {
                let opacity = { x: material.opacity };
                let t = new TWEEN.Tween(opacity).to({ x: target }, 100);
                t.onUpdate(() => {
                    pickSphere.visible = opacity.x > 0;
                    box.visible = opacity.x > 0;
                    material.opacity = opacity.x;
                    pickSphere.material.opacity = opacity.x * 0.5;
                });
                t.start();
            };

            pickSphere.addEventListener('drag', (e) => {});
            pickSphere.addEventListener('mouseup', (e) => {
                e.consume();
            });
            pickSphere.addEventListener('mousedown', (e) => {
                e.consume();
            });
            pickSphere.addEventListener('click', (e) => {
                e.consume();
                this.activeNode = node;
            });
            pickSphere.addEventListener('mouseover', (e) => {});
            pickSphere.addEventListener('mouseleave', (e) => {});
        }
    }

    initializeTranslationHandles() {
        let boxGeometry = new THREE.BoxGeometry(1, 1, 1);

        for (let handleName of Object.keys(this.translationHandles)) {
            let handle = this.handles[handleName];
            let node = handle.node;
            this.scene.add(node);

            let material = new THREE.MeshBasicMaterial({
                color: handle.color,
                opacity: 0.4,
                transparent: true,
            });

            let outlineMaterial = new THREE.MeshBasicMaterial({
                color: 0xcccccc,
                side: THREE.BackSide,
                opacity: 0.4,
                transparent: true,
            });

            let pickMaterial = new THREE.MeshNormalMaterial({
                opacity: 0.2,
                transparent: true,
                visible: this.showPickVolumes,
            });

            let box = new THREE.Mesh(boxGeometry, material);
            box.name = `${handleName}.handle`;
            box.scale.set(0.7, 0.7, 40);
            box.lookAt(new THREE.Vector3(...handle.alignment));
            box.renderOrder = 10;
            node.add(box);
            handle.translateNode = box;

            let outline = new THREE.Mesh(boxGeometry, outlineMaterial);
            outline.name = `${handleName}.outline`;
            outline.scale.set(1.3, 1.3, 1.0);
            outline.renderOrder = 0;
            box.add(outline);

            let pickVolume = new THREE.Mesh(boxGeometry, pickMaterial);
            pickVolume.name = `${handleName}.pick_volume`;
            pickVolume.scale.set(1, 1, 1);
            pickVolume.handle = handleName;
            box.add(pickVolume);
            this.pickVolumes.push(pickVolume);

            node.setOpacity = (target) => {
                let opacity = { x: material.opacity };
                let t = new TWEEN.Tween(opacity).to({ x: target }, 100);
                t.onUpdate(() => {
                    box.visible = opacity.x > 0;
                    pickVolume.visible = opacity.x > 0;
                    material.opacity = opacity.x;
                    outlineMaterial.opacity = opacity.x;
                    pickMaterial.opacity = opacity.x * 0.5;
                });
                t.start();
            };

            pickVolume.addEventListener('drag', (e) => {
                this.dragTranslationHandle(e);
            });
            pickVolume.addEventListener('drop', (e) => {
                this.dropTranslationHandle(e);
            });
        }
    }

    initializeRotationHandles() {
        let adjust = 1.5;
        let torusGeometry = new THREE.TorusGeometry(1, adjust * 0.015, 8, 64, Math.PI / 2);
        let outlineGeometry = new THREE.TorusGeometry(1, adjust * 0.02, 8, 64, Math.PI / 2);
        let pickGeometry = new THREE.TorusGeometry(1, adjust * 0.03, 6, 4, Math.PI / 2);

        for (let handleName of Object.keys(this.rotationHandles)) {
            let handle = this.handles[handleName];
            let node = handle.node;
            this.scene.add(node);

            let material = new THREE.MeshBasicMaterial({
                color: handle.color,
                opacity: 0.4,
                transparent: true,
            });

            let outlineMaterial = new THREE.MeshBasicMaterial({
                color: 0xcccccc,
                side: THREE.BackSide,
                opacity: 0.4,
                transparent: true,
            });

            let pickMaterial = new THREE.MeshNormalMaterial({
                opacity: 0.2,
                transparent: true,
                visible: this.showPickVolumes,
            });

            let box = new THREE.Mesh(torusGeometry, material);
            box.name = `${handleName}.handle`;
            box.scale.set(20, 20, 20);
            box.lookAt(new THREE.Vector3(...handle.alignment));
            node.add(box);
            handle.translateNode = box;

            let outline = new THREE.Mesh(outlineGeometry, outlineMaterial);
            outline.name = `${handleName}.outline`;
            outline.scale.set(1, 1, 1);
            outline.renderOrder = 0;
            box.add(outline);

            let pickVolume = new THREE.Mesh(pickGeometry, pickMaterial);
            pickVolume.name = `${handleName}.pick_volume`;
            pickVolume.scale.set(1, 1, 1);
            pickVolume.handle = handleName;
            box.add(pickVolume);
            this.pickVolumes.push(pickVolume);

            node.setOpacity = (target) => {
                let opacity = { x: material.opacity };
                let t = new TWEEN.Tween(opacity).to({ x: target }, 100);
                t.onUpdate(() => {
                    box.visible = opacity.x > 0;
                    pickVolume.visible = opacity.x > 0;
                    material.opacity = opacity.x;
                    outlineMaterial.opacity = opacity.x;
                    pickMaterial.opacity = opacity.x * 0.5;
                });
                t.start();
            };

            pickVolume.addEventListener('drag', (e) => {
                this.dragRotationHandle(e);
            });
            pickVolume.addEventListener('drop', (e) => {
                this.dropRotationHandle(e);
            });
        }
    }

    dragRotationHandle(e) {
        let drag = e.drag;
        let handle = this.activeHandle;
        let camera = this.viewer.scene.getActiveCamera();

        if (!handle) {
            return;
        }

        let localNormal = new THREE.Vector3(...handle.alignment);
        let n = new THREE.Vector3();
        n.copy(new THREE.Vector4(...localNormal.toArray(), 0).applyMatrix4(handle.node.matrixWorld));
        n.normalize();

        if (!drag.intersectionStart) {
            drag.intersectionStart = drag.location;
            drag.objectStart = drag.object.getWorldPosition(new THREE.Vector3());
            drag.handle = handle;

            let plane = new THREE.Plane().setFromNormalAndCoplanarPoint(n, drag.intersectionStart);

            drag.dragPlane = plane;
            drag.pivot = drag.intersectionStart;
        } else {
            handle = drag.handle;
        }

        this.dragging = true;

        let mouse = drag.end;
        let domElement = this.viewer.renderer.domElement;
        let ray = Utils.mouseToRay(mouse, camera, domElement.clientWidth, domElement.clientHeight);

        let I = ray.intersectPlane(drag.dragPlane, new THREE.Vector3());

        if (I) {
            let center = this.scene.getWorldPosition(new THREE.Vector3());
            let from = drag.pivot;
            let to = I;

            let v1 = from.clone().sub(center).normalize();
            let v2 = to.clone().sub(center).normalize();

            let angle = Math.acos(v1.dot(v2));
            let sign = Math.sign(v1.cross(v2).dot(n));
            angle = angle * sign;
            if (Number.isNaN(angle)) {
                return;
            }

            let updatedObjectLinks = [];
            let normal = new THREE.Vector3(...handle.alignment);
            for (let selection of this.selection) {
                this.viewer.scene.annotations.traverseDescendants((annotation) => {
                    if (annotation.objectLink && selection.userData.objectLink && annotation.objectLink === selection.userData.objectLink) {
                        annotation.localPosition = selection.worldToLocal(annotation.position.clone());
                        annotation.localCameraPosition = selection.worldToLocal(annotation.cameraPosition.clone());
                        annotation.localCameraTarget = selection.worldToLocal(annotation.cameraTarget.clone());
                    }
                });

                selection.rotateOnAxis(normal, angle);
                if (selection.userData.objectLink && !updatedObjectLinks.includes(selection.userData.objectLink)) {
                    this.updateAnnotations(selection);
                    updatedObjectLinks.push(selection.userData.objectLink);
                }
                selection.dispatchEvent({
                    type: 'orientation_changed',
                    object: selection,
                });
            }

            drag.pivot = I;
        }
    }

    dropRotationHandle(e) {
        this.dragging = false;
        this.setActiveHandle(null);
    }

    dragTranslationHandle(e) {
        let drag = e.drag;
        let handle = this.activeHandle;
        let camera = this.viewer.scene.getActiveCamera();

        if (!drag.intersectionStart && handle) {
            drag.intersectionStart = drag.location;
            drag.objectStart = drag.object.getWorldPosition(new THREE.Vector3());

            let start = drag.intersectionStart;
            let dir = new THREE.Vector4(...handle.alignment, 0).applyMatrix4(this.scene.matrixWorld);
            let end = new THREE.Vector3().addVectors(start, dir);
            let line = new THREE.Line3(start.clone(), end.clone());
            drag.line = line;

            let camOnLine = line.closestPointToPoint(camera.position, false, new THREE.Vector3());
            let normal = new THREE.Vector3().subVectors(camera.position, camOnLine);
            let plane = new THREE.Plane().setFromNormalAndCoplanarPoint(normal, drag.intersectionStart);
            drag.dragPlane = plane;
            drag.pivot = drag.intersectionStart;
        } else {
            handle = drag.handle;
        }

        this.dragging = true;

        {
            let mouse = drag.end;
            let domElement = this.viewer.renderer.domElement;
            let ray = Utils.mouseToRay(mouse, camera, domElement.clientWidth, domElement.clientHeight);
            let I = ray.intersectPlane(drag.dragPlane, new THREE.Vector3());

            if (I) {
                let iOnLine = drag.line.closestPointToPoint(I, false, new THREE.Vector3());

                let diff = new THREE.Vector3().subVectors(iOnLine, drag.pivot);
                let updatedObjectLinks = [];
                for (let selection of this.selection) {
                    this.viewer.scene.annotations.traverseDescendants((annotation) => {
                        if (
                            annotation.objectLink &&
                            selection.userData.objectLink &&
                            annotation.objectLink === selection.userData.objectLink
                        ) {
                            annotation.localPosition = selection.worldToLocal(annotation.position.clone());
                            annotation.localCameraPosition = selection.worldToLocal(annotation.cameraPosition.clone());
                            annotation.localCameraTarget = selection.worldToLocal(annotation.cameraTarget.clone());
                        }
                    });

                    selection.position.add(diff);
                    if (selection.userData.objectLink && !updatedObjectLinks.includes(selection.userData.objectLink)) {
                        this.updateAnnotations(selection);
                        updatedObjectLinks.push(selection.userData.objectLink);
                    }
                    selection.dispatchEvent({
                        type: 'position_changed',
                        object: selection,
                    });
                }

                drag.pivot = drag.pivot.add(diff);
            }
        }
    }

    updateAnnotations(model) {
        if (!this.stickyTags) return;
        model.updateMatrixWorld(true); // Update the model's world matrix
        this.viewer.scene.annotations.traverseDescendants((annotation) => {
            if (annotation.objectLink && model.userData.objectLink && annotation.objectLink === model.userData.objectLink) {
                // Convert the annotation's local position to world coordinates
                var updatedPosition = annotation.localPosition.clone();
                updatedPosition.applyMatrix4(model.matrixWorld); // Apply the model's world matrix
                var updateCameraPosition = annotation.localCameraPosition.clone();
                updateCameraPosition.applyMatrix4(model.matrixWorld);
                var updateCameraTarget = annotation.localCameraTarget.clone();
                updateCameraTarget.applyMatrix4(model.matrixWorld);
                // Update the annotation position in its own scene
                annotation.position.copy(updatedPosition);
                annotation.cameraPosition.copy(updateCameraPosition);
                annotation.cameraTarget.copy(updateCameraTarget);
            }
        });
    }

    dropTranslationHandle(e) {
        this.dragging = false;
        this.setActiveHandle(null);
    }

    dropScaleHandle(e) {
        this.dragging = false;
        this.setActiveHandle(null);
    }

    dragScaleHandle(e) {
        let drag = e.drag;
        let handle = this.activeHandle;
        const axis = Utils.extractAxis(handle.name);

        let camera = this.viewer.scene.getActiveCamera();

        if (!drag.intersectionStart) {
            drag.intersectionStart = drag.location;
            drag.objectStart = drag.object.getWorldPosition(new THREE.Vector3());
            drag.handle = handle;

            let start = drag.intersectionStart;
            let dir = new THREE.Vector4(...handle.alignment, 0).applyMatrix4(this.scene.matrixWorld);
            let end = new THREE.Vector3().addVectors(start, dir);
            let line = new THREE.Line3(start.clone(), end.clone());
            drag.line = line;

            let camOnLine = line.closestPointToPoint(camera.position, false, new THREE.Vector3());
            let normal = new THREE.Vector3().subVectors(camera.position, camOnLine);
            let plane = new THREE.Plane().setFromNormalAndCoplanarPoint(normal, drag.intersectionStart);
            drag.dragPlane = plane;
            drag.pivot = drag.intersectionStart;
        } else {
            handle = drag.handle;
        }

        this.dragging = true;

        {
            let mouse = drag.end;
            let domElement = this.viewer.renderer.domElement;
            let ray = Utils.mouseToRay(mouse, camera, domElement.clientWidth, domElement.clientHeight);
            let I = ray.intersectPlane(drag.dragPlane, new THREE.Vector3());

            if (I) {
                let iOnLine = drag.line.closestPointToPoint(I, false, new THREE.Vector3());
                let direction = handle.alignment.reduce((a, v) => a + v, 0);

                let toObjectSpace = this.selection[0].matrixWorld.clone().invert();
                let iOnLineOS = iOnLine.clone().applyMatrix4(toObjectSpace);
                let pivotOS = drag.pivot.clone().applyMatrix4(toObjectSpace);
                let diffOS = new THREE.Vector3().subVectors(iOnLineOS, pivotOS);
                let dragDirectionOS = diffOS.clone().normalize();
                if (iOnLine.distanceTo(drag.pivot) === 0) {
                    dragDirectionOS.set(0, 0, 0);
                }
                let dragDirection = dragDirectionOS.dot(new THREE.Vector3(...handle.alignment));

                let diff = new THREE.Vector3().subVectors(iOnLine, drag.pivot);
                let diffScale = new THREE.Vector3(...handle.alignment).multiplyScalar(diff.length() * direction * dragDirection);
                let diffPosition = diff.clone().multiplyScalar(0.5);

                for (let selection of this.selection) {
                    if (selection.isLibrary) {
                        let uniformScale = Math.max(0.1, selection.scale[axis] + diffScale[axis]);
                        selection.scale.set(uniformScale, uniformScale, uniformScale);
                    } else if (selection instanceof PlaneVolume && selection.maintainAspectRatio) {
                        const aspectRatio = selection.scale.x / selection.scale.y;

                        if (axis === 'x') {
                            const scaleFactor = Math.max(0.1, selection.scale[axis] + diffScale[axis]);
                            selection.scale.set(scaleFactor, scaleFactor / aspectRatio, Math.max(0.1, selection.scale.z + diffScale[axis]));
                        } else if (axis === 'y') {
                            const scaleFactor = Math.max(0.1, selection.scale[axis] + diffScale[axis]);
                            selection.scale.set(scaleFactor * aspectRatio, scaleFactor, Math.max(0.1, selection.scale.z + diffScale[axis]));
                        }
                    } else {
                        selection.scale.add(diffScale);
                        selection.scale.x = Math.max(0.1, selection.scale.x);
                        selection.scale.y = Math.max(0.1, selection.scale.y);
                        selection.scale.z = Math.max(0.1, selection.scale.z);
                    }

                    selection.position.add(diffPosition);

                    selection.dispatchEvent({
                        type: 'position_changed',
                        object: selection,
                    });
                    selection.dispatchEvent({
                        type: 'scale_changed',
                        object: selection,
                    });
                }

                drag.pivot.copy(iOnLine);
            }
        }
    }

    setActiveHandle(handle) {
        if (this.dragging) {
            return;
        }

        if (this.activeHandle === handle) {
            return;
        }

        this.activeHandle = handle;

        if (handle === null) {
            for (let handleName of Object.keys(this.handles)) {
                let handle = this.handles[handleName];
                handle.node.setOpacity(0);
            }
        }

        for (let handleName of Object.keys(this.focusHandles)) {
            let handle = this.focusHandles[handleName];

            if (this.activeHandle === handle) {
                handle.node.setOpacity(1.0);
            } else {
                handle.node.setOpacity(0.4);
            }
        }

        for (let handleName of Object.keys(this.lockHandles)) {
            let handle = this.lockHandles[handleName];

            if (this.activeHandle === handle) {
                handle.node.setOpacity(1.0);
            } else {
                handle.node.setOpacity(0.4);
            }
        }

        for (let handleName of Object.keys(this.textureHandles)) {
            let handle = this.textureHandles[handleName];

            if (this.activeHandle === handle) {
                handle.node.setOpacity(1.0);
            } else {
                handle.node.setOpacity(0.4);
            }
        }

        for (let handleName of Object.keys(this.translationHandles)) {
            let handle = this.translationHandles[handleName];

            if (this.activeHandle === handle) {
                handle.node.setOpacity(1.0);
            } else {
                handle.node.setOpacity(0.4);
            }
        }

        for (let handleName of Object.keys(this.rotationHandles)) {
            let handle = this.rotationHandles[handleName];

            handle.node.setOpacity(0.4);
        }

        for (let handleName of Object.keys(this.scaleHandles)) {
            let handle = this.scaleHandles[handleName];

            if (this.activeHandle === handle) {
                handle.node.setOpacity(1.0);

                let relatedFocusHandle = this.focusHandles[handle.name.replace('scale', 'focus')];
                let relatedFocusNode = relatedFocusHandle.node;
                relatedFocusNode.setOpacity(0.4);

                let relatedLockHandle = this.lockHandles[handle.name.replace('scale', 'lock')];
                let relatedLockNode = relatedLockHandle.node;
                relatedLockNode.setOpacity(0.4);

                for (let translationHandleName of Object.keys(this.translationHandles)) {
                    let translationHandle = this.translationHandles[translationHandleName];
                    translationHandle.node.setOpacity(0.4);
                }
            } else {
                handle.node.setOpacity(0.4);
            }
        }

        if (handle) {
            handle.node.setOpacity(1.0);
        }
    }

    updateScene(boundingBox, scale, rotation, center) {
        let size = boundingBox.getSize(new THREE.Vector3()).multiply(scale);
        // have minimum scale to prevent handles from becoming invisible
        const minScale = 0.001;
        size.x = Math.max(size.x, minScale);
        size.y = Math.max(size.y, minScale);
        size.z = Math.max(size.z, minScale);
        this.scene.scale.copy(size);
        this.scene.position.copy(center);
        this.scene.rotation.copy(rotation);
    }

    getBoundingBox(obj) {
        let objBoundingBox = new THREE.Box3();

        if (obj.constructor.name === 'TilesGroup') {
            obj.tilesRenderer.getBoundingBox(objBoundingBox);
        } else if (obj.visibleBounds) {
            objBoundingBox.copy(obj.visibleBounds);
        } else if (obj.boundingBox) {
            objBoundingBox.copy(obj.boundingBox);
        } else if (obj.geometry && obj.geometry.boundingBox) {
            objBoundingBox.copy(obj.geometry.boundingBox);
        } else if (obj.isFrag) {
            objBoundingBox.copy(obj.getFragmentsModelWorldAABB());
        }
        return objBoundingBox;
    }

    handleSingleSelection(selected, world) {
        let boundingBox = this.getBoundingBox(selected);
        if (!boundingBox) return;

        const worldM = world.clone();

        let rotation = selected.rotation.clone();

        let center;
        center = boundingBox.getCenter(new THREE.Vector3()).clone().applyMatrix4(worldM);

        this.updateScene(boundingBox, selected.scale, rotation, center);
    }

    handleGroupSelection(selectedName, world) {
        let boundingBox = new THREE.Box3();
        this.selection.forEach((obj) => boundingBox.union(obj.boundingBox));

        let center = boundingBox.getCenter(new THREE.Vector3()).clone().applyMatrix4(world);
        this.updateScene(boundingBox, this.selection[0].scale, this.selection[0].rotation, center);
    }

    handleIFCModelSelection(selectedName, world) {
        let boundingBox = new THREE.Box3();
        this.selection.forEach((obj) => {
            if (obj.geometry && obj.geometry.boundingBox) {
                boundingBox.union(obj.geometry.boundingBox);
            }
        });

        let center = boundingBox.getCenter(new THREE.Vector3()).clone().applyMatrix4(world);
        this.updateScene(boundingBox, this.selection[0].scale, this.selection[0].rotation, center);
    }

    handleMultipleSelection() {
        let boundingBox = new THREE.Box3();
        this.selection.forEach((obj) => {
            obj.updateMatrixWorld(true);
            obj.matrixWorldNeedsUpdate = true;

            let objBoundingBox = this.getBoundingBox(obj);
            objBoundingBox.applyMatrix4(obj.matrixWorld);
            boundingBox.union(objBoundingBox);
        });

        let center = boundingBox.getCenter(new THREE.Vector3());
        let size = boundingBox.getSize(new THREE.Vector3());
        this.scene.position.copy(center);
        this.scene.scale.set(size.x, size.y, size.z);
        this.scene.rotation.set(0, 0, 0);
    }

    updateSceneBasedOnSelection(selected, selectedName, world) {
        if (this.selection.length === 1) {
            this.handleSingleSelection(selected, world);
        } else if (this.selection.every((s) => Utils.truncateAfterLastHyphen(s.name) === selectedName && s.isGroup === true)) {
            this.handleGroupSelection(selectedName, world);
        } else if (
            this.selection.every((s) => Utils.truncateAfterLastHyphen(s.name) === selectedName && s.constructor.name === 'IFCModel')
        ) {
            this.handleIFCModelSelection(selectedName, world);
        } else {
            this.handleMultipleSelection();
        }
    }

    updateNodeVisibility(node, selected, handleName, selectedName) {
        const isBoxVolume = selected instanceof BoxVolume;
        const isPlane = selected instanceof PlaneVolume;
        const isRelevantObject = selected.isMesh || selected.isGroup || (selected.isObject3D && !isBoxVolume && !isPlane);
        const hasHandleNameMatch = handleName.includes('scale') || handleName.includes('texture') || handleName.includes('focus');
        const shouldHideForSelectionLength = this.selection.length > 1;
        const allNamesMatch =
            this.selection.every((s) => s.isGroup && Utils.truncateAfterLastHyphen(s.name) === selectedName) ||
            this.selection.every((s) => s.constructor.name === 'IFCModel' && Utils.truncateAfterLastHyphen(s.name) === selectedName);
        const hasRotationHandleName = handleName.includes('rotation');
        const isLibraryWithTextureOrFocus = selected.isLibrary && (handleName.includes('texture') || handleName.includes('focus'));

        if (isPlane) {
            if (selected.locked) {
                if (handleName !== 'lock.z+' && handleName !== 'texture.z+') {
                    node.visible = false;
                    return;
                }
            } else {
                if (
                    handleName === 'scale.z+' ||
                    handleName === 'scale.z-' ||
                    handleName === 'lock.y+' ||
                    handleName === 'lock.y-' ||
                    handleName === 'texture.y+' ||
                    handleName === 'texture.y-' ||
                    handleName === 'focus.y+' ||
                    handleName === 'focus.y-' ||
                    handleName === 'lock.x+' ||
                    handleName === 'lock.x-' ||
                    handleName === 'texture.x+' ||
                    handleName === 'texture.x-' ||
                    handleName === 'focus.x+' ||
                    handleName === 'focus.x-' ||
                    handleName === 'lock.z-' ||
                    handleName === 'lock.z+' ||
                    handleName === 'focus.z-' ||
                    handleName === 'texture.z-'
                ) {
                    node.visible = false;
                    return;
                }
            }
            node.visible = true;
            return;
        }

        if (isBoxVolume || selected.isLibrary) {
            if ((selected.locked ?? false) !== handleName.includes('lock') && !handleName.includes('texture')) {
                node.visible = false;
                return;
            }
        } else if (handleName.includes('lock') && !handleName.includes('texture')) {
            node.visible = false;
            return;
        }

        if (isLibraryWithTextureOrFocus) {
            node.visible = false;
            return;
        }

        if (isRelevantObject && hasHandleNameMatch && !selected.isLibrary) {
            node.visible = false;
            return;
        }

        if (shouldHideForSelectionLength && !allNamesMatch && hasRotationHandleName && !selected.isLibrary) {
            node.visible = false;
            return;
        }

        node.visible = true;
    }

    update() {
        if (this.selection.length > 0) {
            this.scene.visible = true;

            this.scene.updateMatrix();
            this.scene.updateMatrixWorld();

            let selected = this.selection[0];
            let world = selected.matrixWorld;
            let camera = this.viewer.scene.getActiveCamera();
            let domElement = this.viewer.renderer.domElement;
            let mouse = this.viewer.inputHandler.mouse;
            const selectedName = Utils.truncateAfterLastHyphen(selected.name);

            this.updateSceneBasedOnSelection(selected, selectedName, world);

            this.scene.updateMatrixWorld();

            {
                for (let handleName of Object.keys(this.handles)) {
                    let handle = this.handles[handleName];
                    let node = handle.node;

                    this.updateNodeVisibility(node, selected, handleName, selectedName, this.selection);

                    let handlePos = node.getWorldPosition(new THREE.Vector3());
                    let distance = handlePos.distanceTo(camera.position);
                    let pr = Utils.projectedRadius(1, camera, distance, domElement.clientWidth, domElement.clientHeight);

                    let ws = node.parent.getWorldScale(new THREE.Vector3());

                    let s = 7 / pr;
                    let scale = new THREE.Vector3(s, s, s).divide(ws);

                    let rot = new THREE.Matrix4().makeRotationFromEuler(node.rotation);
                    let rotInv = rot.clone().invert();

                    scale.applyMatrix4(rotInv);
                    scale.x = Math.abs(scale.x);
                    scale.y = Math.abs(scale.y);
                    scale.z = Math.abs(scale.z);

                    node.scale.copy(scale);
                }

                if (!this.dragging) {
                    let tWorld = this.scene.matrixWorld;
                    let tObject = tWorld.clone().invert();
                    let camObjectPos = camera.getWorldPosition(new THREE.Vector3()).applyMatrix4(tObject);

                    let x = this.rotationHandles['rotation.x'].node.rotation;
                    let y = this.rotationHandles['rotation.y'].node.rotation;
                    let z = this.rotationHandles['rotation.z'].node.rotation;

                    x.order = 'ZYX';
                    y.order = 'ZYX';

                    let above = camObjectPos.z > 0;
                    let below = !above;
                    let PI_HALF = Math.PI / 2;

                    if (above) {
                        if (camObjectPos.x > 0 && camObjectPos.y > 0) {
                            x.x = 1 * PI_HALF;
                            y.y = 3 * PI_HALF;
                            z.z = 0 * PI_HALF;
                        } else if (camObjectPos.x < 0 && camObjectPos.y > 0) {
                            x.x = 1 * PI_HALF;
                            y.y = 2 * PI_HALF;
                            z.z = 1 * PI_HALF;
                        } else if (camObjectPos.x < 0 && camObjectPos.y < 0) {
                            x.x = 2 * PI_HALF;
                            y.y = 2 * PI_HALF;
                            z.z = 2 * PI_HALF;
                        } else if (camObjectPos.x > 0 && camObjectPos.y < 0) {
                            x.x = 2 * PI_HALF;
                            y.y = 3 * PI_HALF;
                            z.z = 3 * PI_HALF;
                        }
                    } else if (below) {
                        if (camObjectPos.x > 0 && camObjectPos.y > 0) {
                            x.x = 0 * PI_HALF;
                            y.y = 0 * PI_HALF;
                            z.z = 0 * PI_HALF;
                        } else if (camObjectPos.x < 0 && camObjectPos.y > 0) {
                            x.x = 0 * PI_HALF;
                            y.y = 1 * PI_HALF;
                            z.z = 1 * PI_HALF;
                        } else if (camObjectPos.x < 0 && camObjectPos.y < 0) {
                            x.x = 3 * PI_HALF;
                            y.y = 1 * PI_HALF;
                            z.z = 2 * PI_HALF;
                        } else if (camObjectPos.x > 0 && camObjectPos.y < 0) {
                            x.x = 3 * PI_HALF;
                            y.y = 0 * PI_HALF;
                            z.z = 3 * PI_HALF;
                        }
                    }
                }

                {
                    let ray = Utils.mouseToRay(mouse, camera, domElement.clientWidth, domElement.clientHeight);
                    let raycaster = new THREE.Raycaster(ray.origin, ray.direction);
                    let intersects = raycaster.intersectObjects(
                        this.pickVolumes.filter((v) => v.parent.parent.visible && v.visible),
                        true
                    );

                    if (intersects.length > 0) {
                        let I = intersects[0];
                        let handleName = I.object.handle;
                        this.setActiveHandle(this.handles[handleName]);
                    } else {
                        this.setActiveHandle(null);
                    }
                }

                for (let handleName of Object.keys(this.scaleHandles)) {
                    let handle = this.handles[handleName];
                    let node = handle.node;
                    let alignment = handle.alignment;
                }
            }
        } else {
            this.scene.visible = false;
        }
    }
}
